import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import EuroOutlinedIcon from '@mui/icons-material/EuroOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';

import DataTable from '../shared/DataTable';
import PaginationControls from '../shared/PaginationControls';
import usePaginatedList from '../../hooks/usePaginatedList';
import { CARD_SX, IconTile, KIT_DIALOG_TITLE_SX, KitHeader, Overline, RAISED_CARD_SX, TONE, TagChip } from '../shared/designKit';
import { numFont, plateFont } from '../../utils/fonts';
import { MobileCard, MobileEmptyState, MobileListContainer } from './MobileCards';
import {
  STATUS_TONE,
  channelIcon,
  channelShortTranslationKey,
  formatCost,
  skipReasonTranslationKey,
  statusTranslationKey,
} from './reminderUtils';

// The ROI counters: a tile, an overline and a big figure, like the price block of
// Tarjousautomaatti. Conversions get the green tile: that number sells the feature.
function StatCard({ icon, label, value, tone }) {
  return (
    <Box sx={{ ...RAISED_CARD_SX, p: 2, flex: '1 1 160px', minWidth: 150, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <IconTile size={34} tone={tone || TONE.slate}>{icon}</IconTile>
      <Box sx={{ minWidth: 0 }}>
        <Overline sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Overline>
        <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: '-.5px', lineHeight: 1.15, mt: 0.5, whiteSpace: 'nowrap', ...numFont }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

const statusTag = (row, t) => (
  <TagChip label={t(statusTranslationKey(row.status))} tone={STATUS_TONE[row.status] || TONE.slate} />
);

const rowTime = (row) => format(parseISO(row.sent_at || row.scheduled_for || row.created_at), 'd.M.yyyy HH:mm');

// History tab: the audit trail + the ROI counters. "Muistutukset toivat N
// käyntiä tänä vuonna" is the headline that sells the feature.
export default function SentTab({ catalog }) {
  const { t } = useTranslation();
  const orgId = useUserStore((s) => s.currentOrganization?.organization_id);

  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [detailRow, setDetailRow] = useState(null);

  const list = usePaginatedList('/api/reminder-sends/', {
    initialFilters: { organization: orgId },
    defaultPageSize: 25,
  });

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get('/api/reminder-sends/stats/', { params: { organization: orgId } });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load reminder stats:', err);
    }
  }, [orgId]);

  const fetchRules = useCallback(async () => {
    try {
      const response = await axios.get('/api/reminder-rules/', { params: { organization: orgId } });
      setRules(response.data);
    } catch (err) {
      console.error('Failed to load reminder rules:', err);
    }
  }, [orgId]);

  useEffect(() => {
    fetchStats();
    fetchRules();
  }, [fetchStats, fetchRules]);

  const columns = [
    {
      key: 'created_at',
      label: t('service_reminders.col_date'),
      width: '1%',
      noWrap: true,
      render: (row) => <Typography variant="body2" sx={{ ...numFont, color: 'text.secondary' }}>{rowTime(row)}</Typography>,
    },
    {
      key: 'customer',
      label: t('service_reminders.col_customer'),
      maxWidth: 200,
      render: (row) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.customer_name}</Typography>,
    },
    {
      key: 'vehicle',
      label: t('service_reminders.col_vehicle'),
      width: '1%',
      noWrap: true,
      render: (row) => <Typography variant="body2" sx={{ ...plateFont, fontWeight: 600 }}>{row.vehicle_registration}</Typography>,
    },
    {
      key: 'rule',
      label: t('service_reminders.col_rule'),
      maxWidth: 200,
      render: (row) => row.rule_name,
    },
    {
      key: 'channel',
      label: t('service_reminders.col_channel'),
      noWrap: true,
      render: (row) => row.channel_used && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
          {channelIcon(row.channel_used, { fontSize: 16 })}
          <Typography variant="body2" sx={{ color: 'text.primary' }}>{t(channelShortTranslationKey(row.channel_used))}</Typography>
        </Box>
      ),
    },
    {
      key: 'status',
      label: t('service_reminders.col_status'),
      render: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          {statusTag(row, t)}
          {row.status === 'skipped' && row.skip_reason && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }}>
              {t(skipReasonTranslationKey(row.skip_reason))}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'cost',
      label: t('service_reminders.col_cost'),
      width: '1%',
      align: 'right',
      noWrap: true,
      render: (row) => <Typography variant="body2" sx={{ ...numFont }}>{formatCost(row.cost_snapshot)}</Typography>,
    },
    {
      key: 'converted',
      label: t('service_reminders.col_converted'),
      width: '1%',
      align: 'center',
      render: (row) =>
        row.converted ? <CheckCircleOutlineIcon fontSize="small" sx={{ color: TONE.green.color }} titleAccess={t('service_reminders.converted_tooltip')} /> : '',
    },
  ];

  const selectSx = { minWidth: 180, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } };

  return (
    <Box>
      {stats && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <StatCard icon={<SendOutlinedIcon />} label={t('service_reminders.stat_sent_year')} value={stats.sent_year} tone={TONE.accent} />
          <StatCard icon={<DirectionsCarFilledOutlinedIcon />} label={t('service_reminders.stat_conversions')} value={stats.conversions_year} tone={TONE.green} />
          {stats.conversion_revenue !== null && (
            <StatCard icon={<EuroOutlinedIcon />} label={t('service_reminders.stat_revenue')} value={formatCost(stats.conversion_revenue)} tone={TONE.green} />
          )}
          <StatCard icon={<ScheduleOutlinedIcon />} label={t('service_reminders.stat_scheduled')} value={stats.scheduled} />
          {stats.sms_cost_year !== null && (
            <StatCard icon={<SmsOutlinedIcon />} label={t('service_reminders.stat_sms_cost')} value={formatCost(stats.sms_cost_year)} />
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={selectSx}>
          <InputLabel>{t('service_reminders.filter_rule')}</InputLabel>
          <Select
            value={list.filters.rule || 'all'}
            label={t('service_reminders.filter_rule')}
            onChange={(e) => list.updateFilters({ rule: e.target.value })}
          >
            <MenuItem value="all">{t('service_reminders.filter_all')}</MenuItem>
            {rules.map((rule) => (
              <MenuItem key={rule.id} value={rule.id}>{rule.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={selectSx}>
          <InputLabel>{t('service_reminders.filter_status')}</InputLabel>
          <Select
            value={list.filters.status || 'all'}
            label={t('service_reminders.filter_status')}
            onChange={(e) => list.updateFilters({ status: e.target.value })}
          >
            <MenuItem value="all">{t('service_reminders.filter_all')}</MenuItem>
            {['sent', 'scheduled', 'skipped', 'failed'].map((status) => (
              <MenuItem key={status} value={status}>{t(statusTranslationKey(status))}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <DataTable
        columns={columns}
        data={list.data}
        loading={list.loading}
        onRowClick={(row) => setDetailRow(row)}
        emptyIcon={<SendOutlinedIcon />}
        emptyTitle={t('service_reminders.no_sent_title')}
        emptyDescription={t('service_reminders.no_sent_description')}
        testIdPrefix="reminder-send"
        tableTestId="reminder-sends-table"
        pagination={{
          page: list.page,
          totalPages: list.totalPages,
          totalCount: list.totalCount,
          pageSize: list.pageSize,
          onPageChange: list.setPage,
          onPageSizeChange: list.setPageSize,
        }}
      />

      {/* Mobile card list (DataTable hides itself below lg) */}
      <MobileListContainer testId="reminder-sends-mobile">
        {list.loading && list.data.length === 0 ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : list.data.length === 0 ? (
          <MobileEmptyState
            icon={<SendOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />}
            title={t('service_reminders.no_sent_title')}
            description={t('service_reminders.no_sent_description')}
          />
        ) : (
          list.data.map((row) => (
            <MobileCard key={row.id} onClick={() => setDetailRow(row)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', ...numFont }}>{rowTime(row)}</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 0.25, lineHeight: 1.3 }} noWrap>
                    {row.customer_name}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25, ...plateFont }}>{row.vehicle_registration}</Typography>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }} noWrap>
                    {row.rule_name}
                    {row.channel_used ? ` · ${t(channelShortTranslationKey(row.channel_used))}` : ''}
                    {row.cost_snapshot ? ` · ${formatCost(row.cost_snapshot)}` : ''}
                  </Typography>
                  {row.status === 'skipped' && row.skip_reason && (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
                      {t(skipReasonTranslationKey(row.skip_reason))}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                  {statusTag(row, t)}
                  {row.converted && (
                    <CheckCircleOutlineIcon fontSize="small" sx={{ color: TONE.green.color }} titleAccess={t('service_reminders.converted_tooltip')} />
                  )}
                </Box>
              </Box>
            </MobileCard>
          ))
        )}
        {list.data.length > 0 && (
          <PaginationControls
            page={list.page}
            totalPages={list.totalPages}
            totalCount={list.totalCount}
            pageSize={list.pageSize}
            onPageChange={list.setPage}
            onPageSizeChange={list.setPageSize}
            loading={list.loading}
          />
        )}
      </MobileListContainer>

      <Dialog open={!!detailRow} onClose={() => setDetailRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={KIT_DIALOG_TITLE_SX}>
          <KitHeader
            icon={<SendOutlinedIcon />}
            title={t('service_reminders.message_detail_title')}
            subtitle={detailRow ? `${detailRow.customer_name} · ${detailRow.vehicle_registration} · ${rowTime(detailRow)}` : ''}
            onClose={() => setDetailRow(null)}
          />
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'background.default', p: { xs: 2, md: 3 } }}>
          {detailRow && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {statusTag(detailRow, t)}
                {detailRow.channel_used && (
                  <TagChip
                    icon={channelIcon(detailRow.channel_used)}
                    label={t(channelShortTranslationKey(detailRow.channel_used))}
                    tone={TONE.slate}
                  />
                )}
                {detailRow.cost_snapshot && (
                  <TagChip label={formatCost(detailRow.cost_snapshot)} tone={TONE.slate} sx={{ ...numFont }} />
                )}
              </Box>
              {detailRow.message_rendered ? (
                <Box sx={{ ...CARD_SX, p: 2, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5 }}>
                  {detailRow.message_rendered}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {detailRow.status === 'skipped' && detailRow.skip_reason
                    ? t(skipReasonTranslationKey(detailRow.skip_reason))
                    : t('service_reminders.no_message')}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
